import { px } from '@adsk/alloy-react-helpers';

import {
  AlloyLegacyThemeDesignTokens,
  BannerDesignTokens,
  BannerVariantTokens,
  WeaveTheme,
} from '../types';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;
type WeaveBannerTokens = WeaveTokens['component']['banner'];

type BannerVariant = WeaveBannerTokens['info'];

export const getBannerTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): BannerDesignTokens => ({
  container: {
    _: {
      'min-height': px(50),
      color: theme.tokens.colors.text.primary.value,
      'border-width': px(1),
      'border-style': 'solid',
      width: '100%',
      display: 'flex',
      'transition-duration': '0.3s',
      'transition-property': 'transform',
      'transition-timing-function': 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
    inline: {
      true: { 'border-radius': px(theme.borderRadius.medium) },
      false: { position: 'absolute', top: 0, left: 0 },
    },
    open: {
      true: { transform: 'translateY(0)' },
      false: { transform: 'translateY(-100%)' },
    },
  },
  iconContainer: {
    height: 'auto',
    'min-height': px(40),
    'min-width': px(48),
    color: theme.tokens.colors.icon.inverse.value,
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'center',
  },
  messageContainer: {
    flex: 1,
    display: 'flex',
    'flex-direction': 'column',
    'justify-content': 'center',
    'padding-top': px(12),
    'padding-bottom': px(12),
    'padding-left': px(15),
  },
  actionsContainer: {
    _: {
      height: 'auto',
      display: 'flex',
      'align-items': 'center',
      'justify-content': 'space-evenly',
      'flex-direction': 'row',
      '& button': {
        'margin-top': 0,
        'margin-bottom': 0,
        'margin-right': px(5),
        'margin-left': px(5),
      },
    },
    message: {
      true: {},
      false: { 'padding-left': px(15) },
    },
  },
  closeButton: {
    'margin-right': px(6),
  },
  divider: {
    'margin-top': 0,
    'margin-right': px(theme.spacing.XS),
    'margin-bottom': 0,
    'margin-left': px(theme.spacing.XS),
    height: px(32),
  },
  variant: {
    primary: {
      _: {
        'background-color':
          theme.tokens.colors.background.information.subtler.value,
        'border-color': theme.tokens.colors.border.information.value,
      },
      icon: {
        'background-color': theme.tokens.colors.background.information.value,
      },
      divider: {
        'background-color':
          theme.tokens.colors.background.information.subtle.value,
      },
    },
    success: {
      _: {
        'background-color':
          theme.tokens.colors.background.success.subtler.value,
        'border-color': theme.tokens.colors.border.success.value,
      },
      icon: {
        'background-color': theme.tokens.colors.background.success.value,
      },
      divider: {
        'background-color': theme.tokens.colors.background.success.subtle.value,
      },
    },
    warning: {
      _: {
        'background-color':
          theme.tokens.colors.background.warning.subtler.value,
        'border-color': theme.tokens.colors.border.warning.value,
      },

      icon: {
        'background-color': theme.tokens.colors.background.warning.value,
      },
      divider: {
        'background-color': theme.tokens.colors.background.warning.subtle.value,
      },
    },
    error: {
      _: {
        'background-color': theme.tokens.colors.background.error.subtler.value,
        'border-color': theme.tokens.colors.border.error.value,
      },

      icon: {
        'background-color': theme.tokens.colors.background.error.value,
      },
      divider: {
        'background-color': theme.tokens.colors.background.error.subtle.value,
      },
    },
  },
});

export const getBannerTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): BannerDesignTokens => {
  const banner = tokens?.component?.banner;

  const mapVariant = (variant?: BannerVariant): BannerVariantTokens => ({
    _: {
      'background-color': variant?.['message-container']?.['background-color'],
      'border-color': variant?.['message-container']?.['border-color'],
    },
    icon: {
      'background-color': variant?.['icon-container']?.['background-color'],
    },
    divider: {},
  });

  const messageContainer = banner?.['message-container'];
  const closeButton = banner?.['close-button'];
  const child = banner?.child;

  return {
    container: {
      _: {
        'font-family': fontFamily,
        'min-height': banner?.['min-height'],
        color: messageContainer?.['text-color'],
        'border-width': messageContainer?.['border-width'],
        'border-style': 'solid',
        width: '100%',
        display: 'flex',
        'transition-duration': '0.3s',
        'transition-property': 'transform',
        'transition-timing-function': 'cubic-bezier(0.4, 0, 0.2, 1)',
      },
      inline: {
        true: { 'border-radius': tokens?.generic?.['border-radius']?.[4] },
        false: { position: 'absolute', top: 0, left: 0 },
      },
      open: {
        true: { transform: 'translateY(0)' },
        false: { transform: 'translateY(-100%)' },
      },
    },
    iconContainer: {
      height: 'auto',
      'min-width': banner?.['icon-container']?.width,
      display: 'flex',
      'align-items': 'center',
      'justify-content': 'center',
      color: banner?.icon?.fill,
    },
    messageContainer: {
      flex: 1,
      display: 'flex',
      'flex-direction': 'column',
      'justify-content': 'center',
      'padding-top': tokens?.generic?.spacing?.[12],
      'padding-bottom': tokens?.generic?.spacing?.[12],
      'padding-left': messageContainer?.['padding-left'],
      'padding-right': messageContainer?.['padding-right'],
      'font-size': messageContainer?.['font-size'],
      'font-weight': messageContainer?.['font-weight'],
      'line-height': messageContainer?.['line-height'],
    },
    actionsContainer: {
      _: {
        height: 'auto',
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'space-evenly',
        'flex-direction': 'row',
        '& button': {
          'margin-top': child?.['margin-top'],
          'margin-right': child?.['margin-right'],
          'margin-bottom': child?.['margin-bottom'],
          'margin-left': child?.['margin-left'],
        },
      },
      message: {
        true: {},
        false: { 'padding-left': child?.['margin-left'] },
      },
    },
    closeButton: {
      'margin-right': px(6),
      'margin-left': closeButton?.['margin-left'],
    },
    divider: {
      // weave does not have dividers in banner (figma)
      height: 0,
      'margin-top': 0,
      'margin-right': tokens?.generic?.spacing?.xs,
      'margin-bottom': 0,
      'margin-left': tokens?.generic?.spacing?.xs,
    },
    variant: {
      primary: mapVariant(banner?.info),
      success: mapVariant(banner?.success),
      warning: mapVariant(banner?.warning),
      error: mapVariant(banner?.error),
    },
  };
};
